"use client"

import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import {
  ChevronDownSmIcon,
  CheckCircleNeutralIcon,
} from "@/components/ui/icons"

interface FilterPkg {
  id: string
  label: string
}

interface MobileRailFiltersProps {
  panelsLabel: string
  addonsLabel: string
  mainPackages: FilterPkg[]
  addonPackages: FilterPkg[]
  /** Single-select: the currently active panel id (or null). */
  selectedPanel: string | null
  /** Multi-select set of active add-on ids. */
  selectedAddons: Set<string>
  onSelectPanel: (id: string) => void
  onToggleAddon: (id: string) => void
  /** package id → biomarker/condition count shown on each option row. */
  counts: Map<string, number>
}

/**
 * Mobile-only (`md:hidden`) collapse of the explorer's Panels + Add-ons rail
 * groups into two dropdown menus, so the long filter lists don't push the
 * search results down the page. On md+ the rail renders the full vertical
 * lists instead and this component is hidden. Panels are single-select, add-ons
 * multi-select — matching the desktop rail semantics.
 *
 * Whichever trigger is tapped, the menu opens full-width across the toolbar row
 * (anchored to the container, not the half-width trigger) so long option labels
 * never clip. Conditional-mount only (no AnimatePresence), safe under
 * cacheComponents.
 */
export function MobileRailFilters({
  panelsLabel,
  addonsLabel,
  mainPackages,
  addonPackages,
  selectedPanel,
  selectedAddons,
  onSelectPanel,
  onToggleAddon,
  counts,
}: MobileRailFiltersProps) {
  const [open, setOpen] = useState<"panels" | "addons" | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(null)
      }
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  if (mainPackages.length === 0 && addonPackages.length === 0) return null

  const selectedPanelPkg = mainPackages.find((p) => p.id === selectedPanel)
  const panelsSummary = selectedPanelPkg?.label ?? panelsLabel
  const addonsSummary =
    selectedAddons.size > 0
      ? `${addonsLabel} (${selectedAddons.size})`
      : addonsLabel

  const menuItems =
    open === "panels" ? mainPackages : open === "addons" ? addonPackages : []
  const isChecked = (id: string) =>
    open === "panels" ? selectedPanel === id : selectedAddons.has(id)
  const onPick = (id: string) => {
    if (open === "panels") {
      onSelectPanel(id)
      setOpen(null)
    } else {
      onToggleAddon(id)
    }
  }

  const renderTrigger = (
    key: "panels" | "addons",
    summary: string,
    active: boolean
  ) => (
    <button
      type="button"
      aria-expanded={open === key}
      aria-haspopup="listbox"
      onClick={() => setOpen((cur) => (cur === key ? null : key))}
      className={cn(
        "flex w-full cursor-pointer items-center justify-between gap-2 rounded-2xl border px-4 py-2.5 transition-colors",
        active
          ? "border-neutral-900 bg-neutral-900 text-white"
          : "border-neutral-100 bg-white text-neutral-700"
      )}
    >
      <span className="type-title-md truncate">{summary}</span>
      <ChevronDownSmIcon
        className={cn(
          "shrink-0 transition-transform duration-200",
          open === key && "rotate-180"
        )}
      />
    </button>
  )

  return (
    <div ref={rootRef} className="relative flex gap-2 md:hidden">
      {mainPackages.length > 0 && (
        <div className="min-w-0 flex-1">
          {renderTrigger("panels", panelsSummary, Boolean(selectedPanel))}
        </div>
      )}
      {addonPackages.length > 0 && (
        <div className="min-w-0 flex-1">
          {renderTrigger("addons", addonsSummary, selectedAddons.size > 0)}
        </div>
      )}

      {open && (
        <ul
          role="listbox"
          className="absolute top-full right-0 left-0 z-40 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl bg-white p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-neutral-200"
        >
          {menuItems.map((pkg) => {
            const checked = isChecked(pkg.id)
            return (
              <li key={pkg.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => onPick(pkg.id)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors",
                    checked ? "bg-neutral-100" : "hover:bg-neutral-50"
                  )}
                >
                  {checked ? (
                    <CheckCircleNeutralIcon
                      size={20}
                      className="shrink-0 text-neutral-900"
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="size-5 shrink-0 rounded-full border border-neutral-300 bg-white"
                    />
                  )}
                  <span
                    className={cn(
                      "type-title-md min-w-0 flex-1 truncate",
                      checked && "font-medium text-neutral-900"
                    )}
                  >
                    {pkg.label}
                  </span>
                  <span className="type-body-md text-neutral-400">
                    {counts.get(pkg.id) ?? 0}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
